import { supabase, handleSupabaseError, withRetry, getNetworkStatus } from '../lib/supabase';

// Default menu settings for offline fallback
const defaultMenuSettings = {
  show_homepage: true,
  show_about_us: true,
  show_services: true,
  show_service_details: true,
  show_projects_gallery: true,
  show_resources_hub: true,
  show_e_commerce_store: true,
  show_smart_contact: true,
  show_customer_portal: true,
  show_admin_dashboard: true,
  show_payment_checkout: true,
  show_payment_status_tracking: true,
  show_login: true
};

export class MenuService {
  // Get menu settings with enhanced error handling
  static async getMenuSettings() {
    try {
      // Check network status first
      if (!getNetworkStatus()) {
        console.warn('Network offline - returning default menu settings');
        return defaultMenuSettings;
      }

      const operation = async () => {
        const { data, error } = await supabase?.from('menu_visibility_settings')?.select('*')?.single();
        
        if (error) {
          // If no data exists (404), that's expected for new databases
          if (error?.code === 'PGRST116' || error?.message?.includes('No rows returned')) {
            console.info('No menu settings found in database - using defaults');
            return defaultMenuSettings;
          }
          throw error;
        }
        
        return data || defaultMenuSettings;
      };

      const result = await withRetry(operation, 2, 1000);
      
      // Merge with defaults to ensure all properties exist
      return { ...defaultMenuSettings, ...result };
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'MenuService.getMenuSettings');
      
      // Return default settings for network errors or missing table
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('relation') ||
          !getNetworkStatus()) {
        console.warn('Menu settings load error, using defaults:', errorInfo);
        return defaultMenuSettings;
      }
      
      // For other errors, still return defaults but log more details
      console.error('Menu settings error:', errorInfo);
      return defaultMenuSettings;
    }
  }

  // Update menu settings
  static async updateMenuSettings(settings) {
    try {
      if (!getNetworkStatus()) {
        throw new Error('Cannot update menu settings while offline');
      }

      const operation = async () => {
        // First try to update existing record
        let { data, error } = await supabase?.from('menu_visibility_settings')?.update(settings)?.select()?.single();
        
        if (error) {
          // If no existing record, insert new one
          if (error?.code === 'PGRST116' || error?.message?.includes('No rows returned')) {
            const { data: insertData, error: insertError } = await supabase?.from('menu_visibility_settings')?.insert([settings])?.select()?.single();
            
            if (insertError) {
              throw insertError;
            }
            
            return insertData;
          }
          throw error;
        }
        
        return data;
      };

      return await withRetry(operation, 2, 1000);
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'MenuService.updateMenuSettings');
      throw new Error(`Failed to update menu settings: ${errorInfo.message}`);
    }
  }

  // Initialize menu settings (for first-time setup)
  static async initializeMenuSettings() {
    try {
      if (!getNetworkStatus()) {
        throw new Error('Cannot initialize menu settings while offline');
      }

      const operation = async () => {
        const { data, error } = await supabase?.from('menu_visibility_settings')?.insert([defaultMenuSettings])?.select()?.single();
        
        if (error) {
          throw error;
        }
        
        return data;
      };

      return await withRetry(operation, 2, 1000);
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'MenuService.initializeMenuSettings');
      throw new Error(`Failed to initialize menu settings: ${errorInfo.message}`);
    }
  }

  // Connection status check
  static async checkConnection() {
    try {
      if (!getNetworkStatus()) {
        return false;
      }

      const { error } = await supabase?.from('menu_visibility_settings')?.select('count')?.limit(1);
      return !error;
    } catch (error) {
      return false;
    }
  }
}